import { getTranslation } from 'common';
import { useTranslations } from 'hooks';
import React, { useState } from 'react';
import { Stack, Typography, Box, Button, Dialog, DialogContent, IconButton, Grid } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import MonitorIcon from '@mui/icons-material/Monitor';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import AiModelBuilderImage from "image/AiModelBuilder.png";
import ActivitiesImage from "image/Activities.png";
import StatisticsImage from "image/Statistics.png";
import RobotImage from "image/Robots.png";
import MonitorImage from "image/Monitor.png";
import TimerStartEvent from "image/TimerStartEvent.png";
import RobotShop from "image/RobotShop.png"
import ScheduledTasks from "image/ScheduledTasks.png"
import { Translations } from 'model/messageModel';

const contentSections = (translations: Translations) => [
    {
        title: getTranslation(translations, "landing.content.title1"),
        description: getTranslation(translations, "landing.content.description1"),
        image: AiModelBuilderImage,
        alt: "AI Model Builder"
    },
    {
        title: getTranslation(translations, "landing.content.title2"),
        description: getTranslation(translations, "landing.content.description2"),
        image: ActivitiesImage,
        alt: "Activities"
    },
    {
        title: getTranslation(translations, "landing.content.title3"),
        description: getTranslation(translations, "landing.content.description3"),
        image: StatisticsImage,
        alt: "Statistics"
    },
    {
        title: getTranslation(translations, "landing.content.title4"),
        description: getTranslation(translations, "landing.content.description4"),
        image: RobotImage,
        alt: "Crea i tuoi Robot"
    },
    {
        title: getTranslation(translations, "landing.content.title5"),
        description: getTranslation(translations, "landing.content.description5"),
        image: RobotShop,
        alt: "Shop Robot"
    },
    {
        title: getTranslation(translations, "landing.content.title6"),
        description: getTranslation(translations, "landing.content.description6"),
        image: MonitorImage,
        alt: "Monitor"
    },
    {
        title: getTranslation(translations, "landing.content.title7"),
        description: getTranslation(translations, "landing.content.description7"),
        image: TimerStartEvent,
        alt: "Timer Start Event"
    },
    {
        title: getTranslation(translations, "landing.content.title8"),
        description: getTranslation(translations, "landing.content.description8"),
        image: ScheduledTasks,
        alt: "Esecuzione Processi Pianificati"
    },
];

const features = (translations: any) => [
    {
        icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
        title: getTranslation(translations, "landing.feature.title1"),
        text: getTranslation(translations, "landing.feature.text1")
    },
    {
        icon: <AccountTreeOutlinedIcon sx={{ fontSize: 40 }} />,
        title: getTranslation(translations, "landing.feature.title2"),
        text: getTranslation(translations, "landing.feature.text2")
    },
    {
        icon: <BarChartOutlinedIcon sx={{ fontSize: 40 }} />,
        title: getTranslation(translations, "landing.feature.title3"),
        text: getTranslation(translations, "landing.feature.text3")
    },
    {
        icon: <PrecisionManufacturingIcon sx={{ fontSize: 40 }} />,
        title: getTranslation(translations, "landing.feature.title4"),
        text: getTranslation(translations, "landing.feature.text4")
    },
    {
        icon: <MonitorIcon sx={{ fontSize: 40 }} />,
        title: getTranslation(translations, "landing.feature.title5"),
        text: getTranslation(translations, "landing.feature.text5")
    },
    {
        icon: <TimerOutlinedIcon sx={{ fontSize: 40 }} />,
        title: getTranslation(translations, "landing.feature.title6"),
        text: getTranslation(translations, "landing.feature.text6")
    }
];

interface LandingCoreProps {
    focusEmailField: () => void;
}

const LandingCore: React.FC<LandingCoreProps> = ({ focusEmailField }) => {
    const translations = useTranslations();
    const [open, setOpen] = useState(false);
    const [zoomedImage, setZoomedImage] = useState<string | null>(null);

    const handleZoom = (src: string) => {
        setZoomedImage(src);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setZoomedImage(null);
    };

    const scrollToTop = () => {
        const element = document.getElementById('email-form-section');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            focusEmailField();
        }
    };

    const ZoomableBox = ({ src, alt }: { src: string, alt: string }) => (
        <Box
            onClick={(e) => {
                const isDesktop = window.matchMedia('(min-width:900px)').matches;
                if (isDesktop) {
                    handleZoom(src);
                }
            }}
            sx={{
                position: 'relative',
                width: { xs: '100%', md: '800px' },
                aspectRatio: '16/9',
                borderRadius: 2,
                boxShadow: 6,
                mt: { xs: 4, md: 0 },
                cursor: 'pointer',
            }}>
            <LazyLoadImage
                alt={alt}
                src={src}
                effect="blur"
                width="100%"
                height="100%"
                style={{ display: 'block', objectFit: 'cover', borderRadius: '8px' }}
            />
            <IconButton
                onClick={(e) => {
                    e.stopPropagation();
                    handleZoom(src);
                }}
                aria-label={getTranslation(translations, "landing.zoom_image")}
                sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    position: 'absolute',
                    top: 0,
                    right: 8,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    boxShadow: 1,
                    '&:hover': { backgroundColor: 'white' }
                }}
            >
                <ZoomInIcon />
            </IconButton>
        </Box>
    );

    return (
        <Stack>
            <Typography textAlign="center" variant='h1' mb="30px">{getTranslation(translations, "landing.title")}</Typography>

            {/* Features Section */}
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Grid
                    container
                    spacing={{ xs: 4, md: 8 }}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ maxWidth: 1200, width: '100%', px: 2 }}
                >
                    {features(translations).map((feature, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
                                <Box sx={{ color: 'primary.main' }}>{feature.icon}</Box>
                                <Typography variant="h5" sx={{ textAlign: 'center' }}>
                                    {feature.title}
                                </Typography>
                                <Typography variant="caption" sx={{ textAlign: 'center' }}>
                                    {feature.text}
                                </Typography>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* image and text Section */}
            <Box sx={{ position: 'relative', py: 6 }}>
                {/* linea centrale*/}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 'calc(8rem + 30px)', //la toglie da sopra la prima
                        bottom: 'calc(8rem + 30px)', // la toglie da sotto l'ultima
                        left: '50%',
                        width: '2px',
                        backgroundColor: 'primary.main',
                        transform: 'translateX(-50%)',
                        zIndex: 0,
                    }}
                />
                {/* sezioni */}
                {contentSections(translations).map((section, index) => {
                    const isReversed = index % 2 !== 0;
                    return (
                        <Stack
                            key={index}
                            justifyContent="space-around"
                            alignItems="center"
                            mt={30}
                            sx={{
                                position: 'relative',
                                backgroundColor: 'grey.50',
                                maxWidth: 1160,
                                p: 8,
                                mx: "auto",
                                flexDirection: {
                                    xs: 'column',
                                    md: isReversed ? 'row-reverse' : 'row'
                                },
                                zIndex: 1
                            }}
                        >
                            <Stack spacing={2} sx={{ maxWidth: '380px', [isReversed ? 'ml' : 'mr']: { xs: 0, md: 4 } }}>
                                <Typography variant='h1' textAlign="left">{section.title}</Typography>
                                <Typography variant='caption' textAlign="left">{section.description}</Typography>
                                <Button variant='contained' onClick={scrollToTop} sx={{ maxWidth: "200px" }}>{getTranslation(translations, "landing.try_free")}</Button>
                            </Stack>
                            <ZoomableBox src={section.image} alt={section.alt} />
                        </Stack>
                    );
                })}
            </Box>


            {/* Dialog for zoom */}
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen
                PaperProps={{
                    sx: {
                        width: '80vw',
                        height: '80vh',
                        maxWidth: '80vw'
                    }
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}
                >
                    {zoomedImage && (
                        <Box
                            component="img"
                            src={zoomedImage}
                            alt="Zoomed"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Stack >
    );
};

export default LandingCore;